import { KpiTile } from './DashboardCards'
import { DeltaBadge } from '../DeltaBadge'

import type { BoardShortcuts } from '../../../hooks/useBoardShortcuts'
import type { DashboardInsights } from '../../../hooks/useDashboardInsights'

/* Fragments of KPI tiles — the caller supplies the grid, so the combined Dashboard tab and the
   per-board overlay windows lay the SAME tiles out at their own widths. */

/** The team's four issue headlines: Total issues · Completed · Incomplete (Δ) · Important.
 *  "—" while the feed cold-loads (never a fake 0). */
export function IssueKpiTiles({ i, go }: { i: DashboardInsights; go: BoardShortcuts }) {
  const num = (n: number): string => (i.issuesPending ? '—' : String(n))
  return (
    <>
      <KpiTile
        value={num(i.totalIssues)}
        label="Total issues"
        sub="raised in this period"
        onClick={() => go.goIssuesAll()}
      />
      <KpiTile
        value={num(i.closedInPeriod)}
        label="Completed"
        sub="closed in this period"
        onClick={() => go.goIssuesClosed()}
      />
      <KpiTile
        value={num(i.signals.unresolved)}
        label="Incomplete"
        sub={i.issuesPending ? 'loading…' : `open · ${i.signals.dueOrOverdue} breaching target`}
        delta={i.issuesDelta !== null ? <DeltaBadge lens="issues" delta={i.issuesDelta} /> : undefined}
        onClick={() => go.goIssues()}
      />
      <KpiTile
        value={num(i.signals.criticalOpen)}
        label="Important"
        sub={`critical open · target ${i.targets.critical}d`}
        tone={i.signals.criticalOpen > 0 ? 'red' : 'ink'}
        onClick={() => go.goIssues('critical')}
      />
    </>
  )
}

/** The coverage quartet: Coverage % (Δ) · Surveyed · Overdue sites · Never surveyed. */
export function CoverageKpiTiles({ i, go }: { i: DashboardInsights; go: BoardShortcuts }) {
  return (
    <>
      <KpiTile
        value={i.coveragePct !== null ? `${i.coveragePct}%` : '—'}
        label="Coverage"
        sub={`${i.surveyed} of ${i.siteTotal} sites surveyed`}
        delta={i.coverageDelta !== null ? <DeltaBadge lens="coverage" delta={i.coverageDelta} /> : undefined}
        onClick={() => go.goCoverage()}
      />
      <KpiTile
        value={String(i.surveyed)}
        label="Surveyed"
        sub="sites fully surveyed"
        onClick={() => go.goCoverage('surveyed')}
      />
      <KpiTile
        value={String(i.overdueSites)}
        label="Overdue sites"
        sub="survey past its due date"
        tone={i.overdueSites > 0 ? 'red' : 'ink'}
        onClick={() => go.goCoverage('overdue')}
      />
      <KpiTile
        value={String(i.neverSurveyed)}
        label="Never surveyed"
        sub="no first visit yet"
        onClick={() => go.goCoverage('never_surveyed')}
      />
    </>
  )
}
